import React, { useCallback, useMemo, useState } from 'react';
import {
  Platform,
  View,
  Text,
  TouchableOpacity,
} from 'react-native';
import { showAlert, showConfirm } from '@/utils/common/alert';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { EditEventSkeleton } from './EditEventSkeleton';
import { useResponsive } from '@/hooks/common/useResponsive';
import { WebConfirmModal } from '@/components/common/modals';
import { useUpdateEventMutation, useDeleteEventMutation, convertToFormData } from '@/store/api/admin/eventsApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { isNetworkError } from '@/utils/admin/errorHandling';
import { useEditEventForm } from '@/components/admin/events/CreateEvent/hooks/useEditEventForm';
import { useDatePicker } from '@/components/admin/events/CreateEvent/hooks/useDatePicker';
import { EventFormSections } from '@/components/admin/events/CreateEvent/components/EventFormSections';
import { EventDateTimePickers } from '@/components/admin/events/CreateEvent/components/EventDateTimePickers';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { DesktopFormPage } from '@/components/common/admin/desktop/DesktopFormPage';
import { MobileFormScreen } from '@/components/common/admin/mobile/MobileFormScreen';
import { useGetEventsQuery } from '@/store/api/admin/eventsApi';
import { IEvent } from '@/types/admin/events';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { PageHeader } from '@/components/shared/redesign';

type EditEventScreenProps = StackScreenProps<AdminStackParamList, 'EditEvent'>;

export const EditEventScreen: React.FC<EditEventScreenProps> = ({
  navigation,
  route,
}) => {
  const { layoutMode } = useResponsive();
  const { redesign, semantic, admin } = useAdminColors();
  const { eventId } = route.params;
  const { showSuccess, showLoading, hideLoading, showWarning } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Get events data to find the specific event
  const { data: eventsData, isLoading: isLoadingEvents } = useGetEventsQuery({
    sortModel: [],
    filterModel: { items: [] },
    paginationModel: { page: 0, pageSize: 1000 },
    selectedPropertyId: selectedPropertyId || 0,
  }, {
    skip: !selectedPropertyId,
  });

  // Find the specific event
  const event = useMemo(() => {
    if (!eventsData?.data) return null;
    return eventsData.data.find((e: IEvent) => e.id === eventId) || null;
  }, [eventsData, eventId]);

  // Custom hooks for form and date picker logic
  const {
    formData,
    errors,
    hasChanges,
    validateForm,
    handleFieldChange,
    handlePublishTypeChange,
    appendPhotos,
    showDiscardAlert,
  } = useEditEventForm(event);

  const {
    datePickerState,
    DateTimePicker,
    handleDatePickerChange,
    showStartDatePicker,
    showEndDatePicker,
    showPublishDatePicker,
    hideDatePickers,
    confirmDateSelection,
    showFallbackDatePicker,
    setDatePickerMode,
  } = useDatePicker();

  // Ensure hooks always run in the same order; render loading state in JSX instead
  const isBusy = isLoadingEvents || !event;

  // Memoized date selection handler to prevent unnecessary re-renders
  const handleDateSelection = useCallback((date: Date, type: 'start' | 'end' | 'publish') => {
    if (type === 'start') {
      handleFieldChange('startTime', date);
      // Auto-adjust end time if it's before the new start time
      if (date >= formData.endTime) {
        const newEndTime = new Date(date);
        newEndTime.setHours(date.getHours() + 1, 0, 0, 0);
        handleFieldChange('endTime', newEndTime);
      }
    } else if (type === 'end') {
      handleFieldChange('endTime', date);
    } else if (type === 'publish') {
      handleFieldChange('publishTime', date);
    }
  }, [formData.endTime, handleFieldChange]);

  // Date picker handlers with fallback support
  const handleStartTimePress = useCallback((anchor?: AnchorRect) => {
    if (Platform.OS === 'web') {
      setWebAnchor(anchor || null);
      showStartDatePicker(formData.startTime);
      return;
    }
    if (DateTimePicker) {
      showStartDatePicker(formData.startTime);
    } else {
      showFallbackDatePicker('Start Time', formData.startTime, (date) => {
        handleDateSelection(date, 'start');
      });
    }
  }, [DateTimePicker, formData.startTime, showStartDatePicker, showFallbackDatePicker, handleDateSelection]);

  const handleEndTimePress = useCallback((anchor?: AnchorRect) => {
    if (Platform.OS === 'web') {
      setWebAnchor(anchor || null);
      showEndDatePicker(formData.endTime);
      return;
    }
    if (DateTimePicker) {
      showEndDatePicker(formData.endTime);
    } else {
      showFallbackDatePicker('End Time', formData.endTime, (date) => {
        handleDateSelection(date, 'end');
      });
    }
  }, [DateTimePicker, formData.endTime, showEndDatePicker, showFallbackDatePicker, handleDateSelection]);

  const handlePublishTimePress = useCallback((anchor?: AnchorRect) => {
    if (Platform.OS === 'web') {
      setWebAnchor(anchor || null);
      showPublishDatePicker(formData.publishTime || new Date());
      return;
    }
    if (DateTimePicker) {
      showPublishDatePicker(formData.publishTime || new Date());
    } else {
      showFallbackDatePicker('Publish Time', formData.publishTime || new Date(), (date) => {
        handleDateSelection(date, 'publish');
      });
    }
  }, [DateTimePicker, formData.publishTime, showPublishDatePicker, showFallbackDatePicker, handleDateSelection]);

  // Web-only anchor state for dropdown positioning
  const [webAnchor, setWebAnchor] = useState<AnchorRect | null>(null);

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    if (!selectedPropertyId) {
      showAlert('Error', 'No property selected');
      return;
    }

    if (!event) {
      return;
    }

    const loadingId = showLoading('Updating event...');
    try {
      // Convert form data to the format expected by the API
      const eventData = {
        name: formData.name,
        description: formData.description,
        location: formData.location,
        startTime: formData.startTime.toISOString(),
        endTime: formData.endTime.toISOString(),
        rsvpLimit: parseInt(formData.rsvpLimit, 10),
        internalNotes: formData.internalNotes,
        publishType: formData.publishType,
        publishTime: formData.publishTime?.toISOString(),
        photos: formData.photos,
      };

      const formDataToSend = convertToFormData(eventData, selectedPropertyId, event.id);
      // Add the event ID for updating
      formDataToSend.append('id', event.id.toString());
      
      const result = await updateEvent(formDataToSend).unwrap();
      
      hideLoading(loadingId);
      
      // Check for partial success (event updated but photos failed to upload)
      if (result.warning) {
        showWarning(`Event updated successfully, but ${result.warning}`, {
          duration: 6000, // Longer duration for important warning
        });
      } else {
        showSuccess('Event updated successfully!');
      }
      navigation.goBack();
    } catch (error: any) {
      hideLoading(loadingId);
      console.error('Failed to update event:', error);
      
      // Improved error message for network issues
      if (isNetworkError(error)) {
        showApiError(error, 'Network error. Please check your connection and try again.');
      } else {
        showApiError(error, 'Failed to update event');
      }
    }
  }, [
    validateForm,
    selectedPropertyId,
    formData,
    event?.id,
    updateEvent,
    showSuccess,
    showWarning,
    showLoading,
    hideLoading,
    showApiError,
    navigation,
  ]);

  // Delete handler
  const handleDelete = useCallback(async () => {
    if (!event) {
      return;
    }
    const loadingId = showLoading('Deleting event...');
    try {
      await deleteEvent(event).unwrap();
      hideLoading(loadingId);
      showSuccess('Event deleted successfully!');
      navigation.goBack();
    } catch (error: any) {
      hideLoading(loadingId);
      console.error('Failed to delete event:', error);
      showApiError(error, 'Failed to delete event');
    }
  }, [deleteEvent, event, showSuccess, showLoading, hideLoading, showApiError, navigation]);

  // Cancel handler
  const handleCancel = useCallback(() => {
    // On web, show modal instead of native alert
    if (Platform.OS === 'web') {
      setShowDiscardModal(true);
      return;
    }
    showDiscardAlert(() => navigation.goBack());
  }, [showDiscardAlert, navigation, hasChanges]);

  // Date picker confirmation handler
  const handleDatePickerConfirm = useCallback((pickerType: 'start' | 'end' | 'publish', selectedDate?: Date) => {
    if (selectedDate) {
      // Android: date passed directly to avoid stale closure
      handleDateSelection(selectedDate, pickerType);
      hideDatePickers();
    } else {
      // iOS/web: read from tempDate state via confirm button
      confirmDateSelection(pickerType, handleDateSelection);
    }
  }, [confirmDateSelection, handleDateSelection, hideDatePickers]);

  // Delete confirmation handler
  const handleDeletePress = useCallback(() => {
    if (Platform.OS === 'web') {
      setShowDeleteModal(true);
      return;
    }
    
    // Use cross-platform confirmation for native
    showConfirm(
      'Delete Event',
      'Are you sure you want to delete this event? This action cannot be undone.',
      'Delete',
      'Cancel'
    ).then((confirmed) => {
      if (confirmed) {
        handleDelete();
      }
    });
  }, [handleDelete]);

  // Memoized scroll content to prevent unnecessary re-renders
  const scrollContent = useMemo(() => (
    <EventFormSections
      formData={formData}
      errors={errors}
      onFieldChange={handleFieldChange}
      appendPhotos={appendPhotos}
      onStartTimePress={handleStartTimePress}
      onEndTimePress={handleEndTimePress}
      onPublishTypeChange={handlePublishTypeChange}
      onPublishTimePress={handlePublishTimePress}
    />
  ), [
    formData,
    errors,
    handleFieldChange,
    appendPhotos,
    handleStartTimePress,
    handleEndTimePress,
    handlePublishTypeChange,
    handlePublishTimePress,
  ]);

  // Desktop web rendering to match admin-web
  if (layoutMode === 'desktop') {
    return (
      <>
        <WebConfirmModal
          visible={showDiscardModal}
          title={'Discard Changes'}
          message={'Are you sure you want to discard your changes?'}
          confirmText={'Discard'}
          cancelText={'Keep Editing'}
          onConfirm={() => {
            setShowDiscardModal(false);
            navigation.goBack();
          }}
          onCancel={() => setShowDiscardModal(false)}
        />
        <WebConfirmModal
          visible={showDeleteModal}
          title={'Delete Event'}
          message={'Are you sure you want to delete this event? This action cannot be undone.'}
          confirmText={'Delete'}
          cancelText={'Cancel'}
          onConfirm={() => {
            setShowDeleteModal(false);
            handleDelete();
          }}
          onCancel={() => setShowDeleteModal(false)}
        />
        <DesktopFormPage
          title="Edit Event"
          subtitle="Update event details"
          isSubmitting={isUpdating || isDeleting}
          submitText="Save"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          renderHeaderActions={() => (
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity
                onPress={handleCancel}
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 10,
                  borderRadius: 8,
                  backgroundColor: redesign.glass.buttonBg,
                  borderWidth: 1,
                  borderColor: redesign.card.border,
                }}
              >
                <Text style={{ color: redesign.text.primary, fontWeight: '600' }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleDeletePress}
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 10,
                  borderRadius: 8,
                  backgroundColor: redesign.glass.buttonBg,
                  borderWidth: 1,
                  borderColor: semantic.status.error,
                }}
              >
                <Text style={{ color: redesign.text.primary, fontWeight: '600' }}>Delete</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleSubmit}
                disabled={isUpdating || isDeleting}
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 10,
                  borderRadius: 8,
                  backgroundColor: admin.formInput.borderFocused,
                  opacity: isUpdating || isDeleting ? 0.7 : 1,
                }}
              >
                <Text style={{ color: '#FFFFFF', fontWeight: '600' }}>Save</Text>
              </TouchableOpacity>
            </View>
          )}
        >
          {isBusy ? (
            <EditEventSkeleton />
          ) : (
            scrollContent
          )}
        </DesktopFormPage>
        <EventDateTimePickers
          DateTimePicker={DateTimePicker}
          datePickerState={datePickerState}
          onDateChange={handleDatePickerChange}
          onConfirm={handleDatePickerConfirm}
          onCancel={hideDatePickers}
          onModeChange={setDatePickerMode}
          webAnchor={webAnchor}
        />
      </>
    );
  }

  return (
    <>
      {/* Web modals for mobile layout */}
      <WebConfirmModal
        visible={showDiscardModal}
        title={'Discard Changes'}
        message={'Are you sure you want to discard your changes?'}
        confirmText={'Discard'}
        cancelText={'Keep Editing'}
        onConfirm={() => {
          setShowDiscardModal(false);
          navigation.goBack();
        }}
        onCancel={() => {
          setShowDiscardModal(false);
        }}
      />
      <WebConfirmModal
        visible={showDeleteModal}
        title={'Delete Event'}
        message={'Are you sure you want to delete this event? This action cannot be undone.'}
        confirmText={'Delete'}
        cancelText={'Cancel'}
        onConfirm={() => {
          setShowDeleteModal(false);
          handleDelete();
        }}
        onCancel={() => setShowDeleteModal(false)}
      />
      
      <MobileFormScreen
        screenName="EditEvent"
        navigation={navigation}
        isSubmitting={isUpdating || isDeleting}
        submitText="Update"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      >
        <PageHeader
          title="Edit event"
          subtitle={event?.name || 'Update event details'}
        />
        {isBusy ? (
          <EditEventSkeleton />
        ) : (
          scrollContent
        )}
        <EventDateTimePickers
          DateTimePicker={DateTimePicker}
          datePickerState={datePickerState}
          onDateChange={handleDatePickerChange}
          onConfirm={handleDatePickerConfirm}
          onCancel={hideDatePickers}
          onModeChange={setDatePickerMode}
          webAnchor={webAnchor}
        />
      </MobileFormScreen>
    </>
  );
};

